// src/components/layout/UserMenuPortal.tsx
// ============================================================
// Summit League v2 — Desktop User Menu (Portaled)
// Dropdown rendered in document.body to avoid stacking context
// issues from navbar backdrop-filter/overflow.
// ============================================================

import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// ── Types ──────────────────────────────────────────────────

interface UserMenuPortalProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onProfile: () => void;
  onTeam: () => void;
  onLogout: () => void;
  displayName: string;
}

// ── Component ──────────────────────────────────────────────

export default function UserMenuPortal({
  open,
  anchorRef,
  onClose,
  onProfile,
  onTeam,
  onLogout,
  displayName,
}: UserMenuPortalProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  // Compute position from anchor element
  const computePosition = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: Math.round(rect.bottom + 8),
      right: Math.round(window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (open) computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => computePosition();

    window.addEventListener('resize', handleReposition, { passive: true });
    window.addEventListener('scroll', handleReposition, {
      passive: true,
      capture: true,
    });

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="sl-nav-user-menu sl-nav-user-menu--portal"
      style={{ top: `${pos.top}px`, right: `${pos.right}px` }}
      role="menu"
      aria-label="User menu"
    >
      <div className="sl-nav-user-menu-label">{displayName}</div>

      <button
        type="button"
        className="sl-nav-user-menu-item"
        role="menuitem"
        onClick={onProfile}
      >
        View Profile
      </button>

      <button
        type="button"
        className="sl-nav-user-menu-item"
        role="menuitem"
        onClick={onTeam}
      >
        My Team / Create Team
      </button>

      <div className="sl-nav-user-menu-divider" />

      <button
        type="button"
        className="sl-nav-user-menu-item sl-nav-user-menu-item--danger"
        role="menuitem"
        onClick={onLogout}
      >
        Logout
      </button>
    </div>,
    document.body
  );
}
